import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Clip, Track, istGespraech, type Op } from '@schnitt/shared';
import { api, type KiVorschlagView, type TranscriptSummary, type VorlageView } from '../api';
import { ausDatei, useEditor } from '../store';
import { newId } from '../ids';
import { Sheet } from './Sheet';

/**
 * **KI-Schnitt** (7.66; Sascha, 3. November: „Rohmaterial hochladen und
 * schneiden automatisch“ -- und dazu: „ist das UX gut genug um schlau mit
 * der KI zu arbeiten? Sodass ich es dann im Schnitt anpassen kann?“).
 *
 * Drei Schritte, und zwischen jedem entscheidet ein Mensch:
 *
 *  1. *Auftrag*: welche Vorlage, ein Satz Briefing. Das Material ist von
 *     selbst alles mit Ton, und was noch kein Transkript hat, wird beim
 *     „Vorschlag holen“ transkribiert (Sascha, 4. November: „warum muss ich
 *     die Medien erst auswählen … es muss alles automatischer gehen. Auch
 *     transkribieren“). Wer es enger will, klappt die Liste auf.
 *  2. *Vorschlag*: was die KI nehmen würde, als Liste mit dem gesprochenen
 *     Text -- jede Stelle abwählbar, und „nachschärfen“ („kürzer“, „anderer
 *     Einstieg“) baut auf dem auf, was jetzt dasteht.
 *  3. *Übernehmen*: Clips auf eine eigene Spur, mit dem Gesagten als Namen.
 *     Eine gewöhnliche Änderung -- rückgängig zu machen, danach von Hand
 *     und im Team weiterzuschneiden.
 */
export function KiSchnittSheet({ onClose }: { onClose: () => void }): ReactElement {
  const project = useEditor((s) => s.project);
  const assets = useEditor((s) => s.assets);
  const role = useEditor((s) => s.role);
  const dispatch = useEditor((s) => s.dispatch);
  const toast = useEditor((s) => s.toast);
  const timeline = useEditor((s) => s.timeline);

  const [vorlagen, setVorlagen] = useState<VorlageView[]>([]);
  const [abschriften, setAbschriften] = useState<TranscriptSummary[]>([]);
  /** `null`: noch nicht angefasst -- dann gilt alles mit Ton. */
  const [handverlesen, setGewaehlt] = useState<string[] | null>(null);
  const [listeOffen, setListeOffen] = useState(false);
  /** Was gerade passiert, in Worten -- „Transkribiere … 1 von 3 fertig“. */
  const [schritt, setSchritt] = useState<string | null>(null);
  const offen = useRef(true);
  useEffect(
    () => () => {
      offen.current = false;
    },
    [],
  );
  const [vorlageId, setVorlageId] = useState<string | null>(null);
  const [briefing, setBriefing] = useState('');
  const [vorschlag, setVorschlag] = useState<KiVorschlagView | null>(null);
  const [aus, setAus] = useState<Set<string>>(new Set());
  const [rueckmeldung, setRueckmeldung] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const mitTon = useMemo(() => assets.filter((a) => a.hasAudio && a.status === 'ready'), [assets]);
  // Von selbst nur, was spricht (4. November): B-Roll ohne Sprache bleibt
  // draussen und wird nicht transkribiert. Höchstens 20, so viele nimmt der
  // Server.
  const gespraeche = useMemo(() => mitTon.filter((a) => istGespraech(a.sprachanteil)), [mitTon]);
  const gewaehlt = handverlesen ?? gespraeche.slice(0, 20).map((a) => a.id);
  const brollZahl = mitTon.length - gespraeche.length;

  const laden = async () => {
    if (!project) return;
    try {
      const [v, t] = await Promise.all([api.vorlagen('ki'), api.transcripts(project.id)]);
      setVorlagen(v.vorlagen);
      setAbschriften(t.transcripts);
      setVorlageId((alt) => alt ?? v.vorlagen[0]?.id ?? null);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Die Vorlagen liessen sich nicht holen.');
    }
  };
  useEffect(() => {
    void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const neuesteIn = (liste: TranscriptSummary[], assetId: string) =>
    liste.filter((t) => t.asset_id === assetId).sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0] ?? null;
  const abschriftVon = (assetId: string) => neuesteIn(abschriften, assetId);

  /**
   * Sorgt dafür, dass das gewählte Material ein Transkript hat: startet, was
   * fehlt oder gescheitert ist, und wartet, bis alles durch ist. Zurück kommt,
   * was geschnitten werden kann -- was nicht ging, wird genannt und
   * übersprungen, statt den ganzen Auftrag aufzuhalten.
   */
  const transkripteSicherstellen = async (ids: string[]): Promise<string[]> => {
    if (!project) return [];
    let liste = (await api.transcripts(project.id)).transcripts;
    const fehlen = ids.filter((id) => {
      const t = neuesteIn(liste, id);
      return !t || t.status === 'failed';
    });
    for (const id of fehlen) await api.startTranscription(id, 'de');
    const frist = Date.now() + 30 * 60_000;
    for (;;) {
      liste = (await api.transcripts(project.id)).transcripts;
      if (!offen.current) return [];
      setAbschriften(liste);
      const stand = ids.map((id) => neuesteIn(liste, id)?.status ?? 'pending');
      const laufen = stand.filter((x) => x === 'pending' || x === 'running').length;
      if (laufen === 0) break;
      setSchritt(`Transkribiere … ${ids.length - laufen} von ${ids.length} fertig`);
      if (Date.now() > frist) throw new Error('Das Transkribieren dauert ungewöhnlich lange. Später noch einmal versuchen.');
      await new Promise((r) => setTimeout(r, 2500));
    }
    const fertig = ids.filter((id) => neuesteIn(liste, id)?.status === 'ready');
    const nicht = ids.filter((id) => !fertig.includes(id)).map((id) => assets.find((a) => a.id === id)?.name ?? id);
    if (fertig.length === 0) {
      const grund = neuesteIn(liste, ids[0]!)?.error;
      throw new Error(`Transkribieren ging nicht${grund ? `: ${grund.split('\n')[0]}` : '.'} Ist unter Verwaltung → KI die Transkription eingerichtet?`);
    }
    if (nicht.length) toast('info', `Ohne Transkript, daher übersprungen: ${nicht.map((n) => `„${n}“`).join(', ')}.`);
    return fertig;
  };

  const holen = async (vorher?: { abschnitte: string[]; rueckmeldung: string }) => {
    if (!project || !vorlageId || gewaehlt.length === 0) return;
    setBusy(true);
    setFehler(null);
    try {
      setSchritt('Prüfe die Transkripte …');
      const material = await transkripteSicherstellen(gewaehlt);
      if (!offen.current || material.length === 0) return;
      setSchritt(vorher ? 'Die KI schärft nach …' : 'Die KI liest das Material …');
      const { vorschlag: neu } = await api.kiSchnitt(project.id, { vorlageId, briefing, assetIds: material, vorher });
      setVorschlag(neu);
      setAus(new Set());
      setRueckmeldung('');
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Die KI hat nicht geantwortet.');
    } finally {
      setBusy(false);
      setSchritt(null);
    }
  };

  const behalten = vorschlag ? vorschlag.abschnitte.filter((a) => !aus.has(a.id)) : [];
  const dauer = behalten.reduce((s, a) => s + (a.bis - a.von), 0);

  const uebernehmen = () => {
    if (!vorschlag || behalten.length === 0) return;
    const kinds = new Set(behalten.map((a) => assets.find((x) => x.id === a.medium)?.kind));
    const art: Track['kind'] = kinds.has('video') ? 'video' : 'audio';
    let n = 1;
    while (timeline.tracks.some((t) => t.id === `KI${n}`)) n++;
    const spur = `KI${n}`;
    const ops: Op[] = [{ op: 'addTrack', track: Track.parse({ id: spur, kind: art, name: `KI-Rohschnitt ${n > 1 ? n : ''}`.trim() }) } as Op];
    let t = 0;
    for (const a of behalten) {
      const laenge = a.bis - a.von;
      ops.push(
        ausDatei({
          op: 'addClip',
          trackId: spur,
          clip: Clip.parse({
            id: newId('c'),
            source: { kind: 'asset', assetId: a.medium },
            start: t,
            duration: laenge,
            sourceIn: a.von,
            label: a.text.slice(0, 80),
          }),
        } as Op),
      );
      t += laenge;
    }
    dispatch(ops);
    toast('success', `KI-Rohschnitt übernommen: ${behalten.length} Stellen, ${Math.round(dauer)} s, auf eigener Spur.`);
    onClose();
  };

  const darf = role !== 'viewer';
  const vorlage = vorlagen.find((v) => v.id === vorlageId) ?? null;

  return (
    <Sheet title="KI-Schnitt" onClose={onClose}>
      {fehler ? (
        <p className="error-text" data-ki-fehler>
          {fehler}
        </p>
      ) : null}

      {!vorschlag ? (
        <>
          <h3 className="ki-titel">Material</h3>
          {mitTon.length === 0 ? <p className="hint">Noch kein Medium mit Ton im Projekt.</p> : null}
          {mitTon.length > 0 ? (
            <div className="ki-material-kurz" data-ki-material-kurz>
              <span>
                {handverlesen === null
                  ? `Alle Gespräche (${gewaehlt.length})${brollZahl ? ` · ${brollZahl} B-Roll ohne Sprache bleibt draussen` : ''}`
                  : `${gewaehlt.length} von ${mitTon.length} ausgewählt`}
                {(() => {
                  const ohne = gewaehlt.filter((id) => abschriftVon(id)?.status !== 'ready').length;
                  return ohne ? ` · ${ohne} ${ohne === 1 ? 'wird' : 'werden'} zuerst transkribiert` : ' · Transkripte da';
                })()}
              </span>
              <button className="small ghost" data-ki-material-aendern onClick={() => setListeOffen(!listeOffen)}>
                {listeOffen ? 'Fertig' : 'Ändern'}
              </button>
            </div>
          ) : null}
          {listeOffen ? (
          <>
          <div className="chips" style={{ margin: '0 0 8px' }}>
            <button className="chip" data-ki-gespraeche onClick={() => setGewaehlt(null)}>
              Nur Gespräche
            </button>
            <button className="chip" data-ki-alle onClick={() => setGewaehlt(mitTon.slice(0, 20).map((a) => a.id))}>
              Alle
            </button>
            <button className="chip" data-ki-keine onClick={() => setGewaehlt([])}>
              Keine
            </button>
          </div>
          <div className="list" data-ki-material={String(mitTon.length)}>
            {mitTon.map((a) => {
              const t = abschriftVon(a.id);
              const an = gewaehlt.includes(a.id);
              return (
                <label className="row" key={a.id} data-ki-medium={a.id}>
                  <input
                    type="checkbox"
                    checked={an}
                    disabled={!darf}
                    onChange={() => setGewaehlt(an ? gewaehlt.filter((x) => x !== a.id) : [...gewaehlt, a.id])}
                    style={{ width: 22, height: 22, minHeight: 0 }}
                  />
                  <div className="grow">
                    <div className="title">{a.name}</div>
                    <div className="sub">
                      {Math.round(a.durationSec)} s ·{' '}
                      {istGespraech(a.sprachanteil) ? '' : 'B-Roll · '}
                      {t?.status === 'ready' ? 'Transkript da' : t && (t.status === 'pending' || t.status === 'running') ? 'Transkript läuft…' : 'noch kein Transkript'}
                    </div>
                  </div>
                  {!t || t.status === 'failed' ? (
                    <button
                      className="small"
                      disabled={!darf || busy}
                      data-ki-transkribieren={a.id}
                      onClick={(e) => {
                        e.preventDefault();
                        void api
                          .startTranscription(a.id, 'de')
                          .then(() => laden())
                          .catch((err: unknown) => setFehler(err instanceof Error ? err.message : 'Das Transkribieren ging nicht los.'));
                      }}
                    >
                      Transkribieren
                    </button>
                  ) : null}
                </label>
              );
            })}
          </div>
          </>
          ) : null}

          <h3 className="ki-titel">Vorlage</h3>
          {vorlagen.length === 0 ? <p className="hint">Keine KI-Vorlagen. Ein Admin legt sie unter Verwaltung → KI an.</p> : null}
          <div className="chips" style={{ marginBottom: 8 }}>
            {vorlagen.map((v) => (
              <button
                key={v.id}
                className={`chip ${vorlageId === v.id ? 'active' : ''}`}
                data-ki-vorlage-wahl={v.name}
                onClick={() => setVorlageId(v.id)}
              >
                {v.name}
              </button>
            ))}
          </div>
          {vorlage ? <p className="hint">{vorlage.satz}</p> : null}

          <label className="field">
            <span>Briefing (optional)</span>
            <textarea
              rows={2}
              maxLength={2000}
              value={briefing}
              data-ki-briefing
              onChange={(e) => setBriefing(e.target.value)}
              placeholder="z. B. Für Instagram, Thema Laternenumzug, Moderator vorne"
            />
          </label>
          <button
            className="primary"
            style={{ width: '100%' }}
            disabled={!darf || busy || gewaehlt.length === 0 || !vorlageId}
            data-ki-holen
            onClick={() => void holen()}
          >
            {busy ? (schritt ?? 'Einen Moment …') : 'Vorschlag holen'}
          </button>
        </>
      ) : (
        <>
          <div className="card" style={{ margin: '0 0 12px' }} data-ki-vorschlag={String(vorschlag.abschnitte.length)}>
            <div className="title">
              {behalten.length} von {vorschlag.gesamt} Stellen · etwa {Math.round(dauer)} s
              {vorschlag.zielSekunden ? ` (Ziel ${Math.round(vorschlag.zielSekunden)} s)` : ''}
            </div>
            {vorschlag.begruendung ? <div className="sub">{vorschlag.begruendung}</div> : null}
            {vorschlag.verworfen.length ? (
              <div className="sub">Verworfen, weil unbrauchbar: {vorschlag.verworfen.length}</div>
            ) : null}
          </div>
          <div className="list" data-ki-stellen>
            {vorschlag.abschnitte.map((a) => (
              <label className="row" key={a.id} data-ki-stelle={a.id}>
                <input
                  type="checkbox"
                  checked={!aus.has(a.id)}
                  onChange={() => {
                    const neu = new Set(aus);
                    if (neu.has(a.id)) neu.delete(a.id);
                    else neu.add(a.id);
                    setAus(neu);
                  }}
                  style={{ width: 22, height: 22, minHeight: 0 }}
                />
                <div className="grow">
                  <div className="title" style={{ whiteSpace: 'normal' }}>
                    {a.text}
                  </div>
                  <div className="sub">
                    {a.name} · {a.von.toFixed(1)}–{a.bis.toFixed(1)} s{a.sprecher ? ` · ${a.sprecher}` : ''}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <label className="field" style={{ marginTop: 12 }}>
            <span>Nachschärfen</span>
            <input
              value={rueckmeldung}
              data-ki-rueckmeldung
              onChange={(e) => setRueckmeldung(e.target.value)}
              placeholder="z. B. kürzer, anderer Einstieg, ohne den Teil über …"
            />
          </label>
          <div className="stack">
            <button
              disabled={!darf || busy}
              data-ki-nachschaerfen
              onClick={() => void holen({ abschnitte: behalten.map((a) => a.id), rueckmeldung })}
            >
              {busy ? (schritt ?? 'Die KI schärft nach …') : 'Nachschärfen'}
            </button>
            <button className="primary" disabled={!darf || busy || behalten.length === 0} data-ki-uebernehmen onClick={uebernehmen}>
              Übernehmen ({behalten.length} Stellen auf eigene Spur)
            </button>
            <button className="ghost" disabled={busy} onClick={() => setVorschlag(null)}>
              Zurück zum Auftrag
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}
